import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useUnit } from "effector-react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import "./OrganizationInfoTab.scss";
import { InputSwitch } from "primereact/inputswitch";
import { Dialog } from "primereact/dialog";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  $sectorsTerms,
  $countriesTerms,
  $organizationEmployeesNumberTerms,
  $organizationCategoriesTerms,
  $programmeTerms,
} from "@/core/store/terms";
import { Term } from "@/core/store/terms/types";

import { countryPhoneCodes } from "@/features/settings/components/countryCodes.json";
import { showToast } from "@/core/store/app";
import { organizationMappers } from "@/core/utils/organizationMappers";
import { DocumentUpload } from "./Documentupload";
import { UploadedDocument } from "../viewmodel/Documentuploadviewmodel";
import { LogoUpload } from "./LogoUpload";

interface OrganizationFormData {
  picNumber: string;
  organizationName: string;
  organizationType: string;
  yearOfEstablishment: string;
  legalForm: string;
  organizationSize: string;
  contactEmail: string;
  country: string;
  city: string;
  contactPhone: string;
  selectedSectors: string[];
  selectedFundingTypes: string[];
  euExpertise: boolean;
  projectsAsLeader?: string;
  projectsAsPartner?: string;
  selectedProgrammes?: string[];
  description: string;
  logoFid: string;
  logoUrl: string;
  documentFids: string[];
  socialProfiles: {
    linkedin: string;
    twitter: string;
    youtube: string;
    instagram: string;
    facebook: string;
    website: string;
  };
}

interface OrganizationInfoTabProps {
  organizationData?: any | null;
  onSave: (payload: any) => Promise<boolean>;
  saveLoading: boolean;
  onDelete: () => Promise<void>;
  deleteLoading: boolean;
  onShowDeleteDialog: () => void;
  onCancelDelete: () => void;
  showDeleteDialog: boolean;
  sectorNames?: string[];
  progress?: number;
}

export const OrganizationInfoTab: React.FC<OrganizationInfoTabProps> = ({
  organizationData,
  onSave,
  saveLoading,
  onDelete,
  deleteLoading,
  onShowDeleteDialog,
  onCancelDelete,
  showDeleteDialog,
  sectorNames,
  progress = 80,
}) => {
  const sectorsTerms = useUnit($sectorsTerms);
  const countriesTerms = useUnit($countriesTerms);
  const organizationEmployeesNumberTerms = useUnit(
    $organizationEmployeesNumberTerms,
  );
  const fundingTypeTerms = useUnit($organizationCategoriesTerms);
  const programmesTerms = useUnit($programmeTerms);

  const [sectorSearch, setSectorSearch] = useState("");
  const [programmeSearch, setProgrammeSearch] = useState("");
  const [selectedCountryCode, setSelectedCountryCode] = useState(
    countryPhoneCodes[0],
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  const [initialDocuments, setInitialDocuments] = useState<UploadedDocument[]>(
    [],
  );

  const [formData, setFormData] = useState<OrganizationFormData>({
    picNumber: "",
    organizationName: "",
    organizationType: "",
    yearOfEstablishment: "",
    legalForm: "Private",
    organizationSize: "",
    contactEmail: "",
    country: "",
    city: "",
    contactPhone: "",
    selectedSectors: [],
    selectedFundingTypes: [],
    euExpertise: true,
    projectsAsLeader: "",
    projectsAsPartner: "",
    selectedProgrammes: [],
    description: "",
    logoFid: "",
    logoUrl: "",
    documentFids: [],
    socialProfiles: {
      linkedin: "",
      twitter: "",
      youtube: "",
      instagram: "",
      facebook: "",
      website: "",
    },
  });

  // Description rich text editor config
  // const descriptionMaxLength = 2000;

  const getTextLength = (html: string) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent?.length || 0;
  };

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ align: [] }],
          ["blockquote"],
          ["link"],
          ["clean"],
        ],
      },
    }),
    [],
  );

  const quillFormats = [
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "align",
    "blockquote",
    "link",
  ];

  // Populate form data when organizationData loads
  useEffect(() => {
    if (
      organizationData &&
      sectorsTerms.data.length > 0 &&
      fundingTypeTerms.data.length > 0
    ) {
      const mappedSectorNames = organizationMappers.mapSectorIdsToNames(
        organizationData.field_sectors,
        sectorsTerms.data,
      );

      // Map field_type TID to name for the Type dropdown
      let organizationTypeName = "";
      if (
        organizationData.field_type &&
        Array.isArray(organizationData.field_type) &&
        organizationData.field_type.length > 0
      ) {
        const typeId = organizationData.field_type[0];
        const matchingType = fundingTypeTerms.data.find(
          (term: Term) => term.tid === typeId,
        );
        organizationTypeName = matchingType?.name || "";
      }

      // Extract document info from API response
      const documents: UploadedDocument[] = [];
      if (organizationData.field_documents) {
        const docs = Array.isArray(organizationData.field_documents)
          ? organizationData.field_documents
          : [organizationData.field_documents];

        docs.forEach((doc: any) => {
          if (doc && doc.fid) {
            documents.push({
              fid: doc.fid.toString(),
              filename: doc.filename || `Document ${doc.fid}`,
              filesize: doc.filesize || 0,
              url: doc.url || "",
            });
          }
        });
      }

      // console.log("Loaded documents from API:", documents);
      setInitialDocuments(documents);

      setFormData((prev) => ({
        ...prev,
        picNumber: organizationData.field_pic_number || "",
        organizationName: organizationData.title || "",
        organizationType: organizationTypeName,
        yearOfEstablishment: organizationData.field_year_of_establishment || "",
        legalForm:
          organizationData.field_legal_form === "1" ? "Public" : "Private",
        organizationSize: organizationData.field_company_size?.[0] || "",
        country: organizationMappers.mapCountryIdToName(
          organizationData.field_country?.[0],
          countriesTerms.data,
        ),
        city: organizationData.field_city || "",
        selectedSectors: mappedSectorNames,
        logoUrl: organizationData.field_logo[0]?.url || "",
        logoFid: organizationData.field_logo[0]?.fid || "",
        description: organizationData.field_description || "",
        contactEmail: organizationData.field_email || "",
        contactPhone: organizationData.field_phone_number || "",
        euExpertise: organizationData.field_expertise === "1" || false,
        projectsAsLeader: organizationData.field_project_leader || "",
        projectsAsPartner: organizationData.field_project_partner || "",
        selectedProgrammes: organizationData.field_programme || [],
        selectedFundingTypes:
          organizationData.field_type?.map((type: any) => type.name) || [],
        socialProfiles: {
          linkedin: organizationData.field_linkedin || "",
          twitter: organizationData.field_twitter || "",
          youtube: organizationData.field_youtube || "",
          instagram: organizationData.field_instagram || "",
          facebook: organizationData.field_facebook || "",
          website: organizationData.field_website_url || "",
        },
        documentFids: documents.map((d) => d.fid),
      }));
    }
  }, [organizationData, sectorsTerms.data, fundingTypeTerms.data]);

  // Set country code from phone number
  useEffect(() => {
    if (formData.contactPhone) {
      const phoneCodeMatch = formData.contactPhone.match(/^\+?\d{1,4}/);
      if (phoneCodeMatch) {
        const phoneCode = phoneCodeMatch[0].startsWith("+")
          ? phoneCodeMatch[0]
          : `+${phoneCodeMatch[0]}`;
        const matchingCountry = countryPhoneCodes.find(
          (country) => country.code === phoneCode,
        );
        if (matchingCountry) {
          setSelectedCountryCode(matchingCountry);
        }
      }
    }
  }, [formData.contactPhone]);

  const organizationTypes = [
    { label: "Private", value: "Private" },
    { label: "Public", value: "Public" },
  ];

  const yearOptions = Array.from({ length: 50 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { label: year.toString(), value: year.toString() };
  });

  // Build sectors tree structure
  type SectorNode = {
    id: string;
    label: string;
    children?: SectorNode[];
  };

  const sectorsTree: SectorNode[] = useMemo(() => {
    if (!sectorsTerms.data || sectorsTerms.data.length === 0) {
      return [];
    }

    const nodes: Record<string, SectorNode> = {};
    const tree: SectorNode[] = [];
    const childIds = new Set<string>();

    sectorsTerms.data.forEach((sector) => {
      nodes[sector.tid] = {
        id: sector.tid,
        label: sector.name,
        children: [],
      };
    });

    sectorsTerms.data.forEach((sector) => {
      if (sector.parent && sector.parent !== "0" && nodes[sector.parent]) {
        nodes[sector.parent].children?.push(nodes[sector.tid]);
        childIds.add(sector.tid);
      }
    });

    sectorsTerms.data.forEach((sector) => {
      if (!childIds.has(sector.tid)) {
        tree.push(nodes[sector.tid]);
      }
    });

    return tree;
  }, [sectorsTerms.data]);

  const [expandedSectors, setExpandedSectors] = useState<Set<string>>(
    new Set(),
  );

  const parseSizeRange = (sizeString: string) => {
    const trimmedString = sizeString.replace(/\s/g, "");
    const parts = trimmedString.split("-");
    if (parts.length === 2) {
      return {
        min: parseInt(parts[0], 10),
        max: parseInt(parts[1], 10),
      };
    } else if (trimmedString.includes("+")) {
      return {
        min: parseInt(trimmedString.replace("+", ""), 10),
        max: Infinity,
      };
    }
    return { min: 0, max: Infinity };
  };

  const organizationSizes = useMemo(() => {
    return organizationEmployeesNumberTerms.data.map((term: Term) => ({
      name: term.name,
      tid: term.tid,
      ...parseSizeRange(term.name),
    }));
  }, [organizationEmployeesNumberTerms.data]);

  const countryOptions = useMemo(() => {
    return countriesTerms.data.map((term: Term) => ({
      label: term.name,
      value: term.name,
      tid: term.tid,
    }));
  }, [countriesTerms.data]);

  const fundingTypeOptions = useMemo(() => {
    return fundingTypeTerms.data.map((term: Term) => ({
      label: term.name,
      value: term.name,
      tid: term.tid,
    }));
  }, [fundingTypeTerms.data]);

  const programmeOptions = useMemo(() => {
    return programmesTerms.data.map((term: Term) => ({
      label: term.name,
      value: term.tid,
      tid: term.tid,
      name: term.name,
      parent: term.parent,
    }));
  }, [programmesTerms.data]);

  // Build programmes tree structure
  type ProgrammeNode = {
    id: string;
    label: string;
    children?: ProgrammeNode[];
  };

  const programmesTree: ProgrammeNode[] = useMemo(() => {
    if (!programmesTerms.data || programmesTerms.data.length === 0) {
      return [];
    }

    const nodes: Record<string, ProgrammeNode> = {};
    const tree: ProgrammeNode[] = [];
    const childIds = new Set<string>();

    programmesTerms.data.forEach((programme) => {
      nodes[programme.tid] = {
        id: programme.tid,
        label: programme.name,
        children: [],
      };
    });

    programmesTerms.data.forEach((programme) => {
      if (
        programme.parent &&
        programme.parent !== "0" &&
        nodes[programme.parent]
      ) {
        nodes[programme.parent].children?.push(nodes[programme.tid]);
        childIds.add(programme.tid);
      }
    });

    programmesTerms.data.forEach((programme) => {
      if (!childIds.has(programme.tid)) {
        tree.push(nodes[programme.tid]);
      }
    });

    return tree;
  }, [programmesTerms.data]);

  const [expandedProgrammes, setExpandedProgrammes] = useState<Set<string>>(
    new Set(),
  );

  const handleSectorRemove = (sectorToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedSectors: prev.selectedSectors.filter(
        (sector) => sector !== sectorToRemove,
      ),
    }));
  };

  const handleToggleExpandSector = (id: string) => {
    setExpandedSectors((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSector = (id: string) => {
    const findNode = (
      nodes: SectorNode[],
      nodeId: string,
    ): SectorNode | null => {
      for (const node of nodes) {
        if (node.id === nodeId) return node;
        if (node.children) {
          const found = findNode(node.children, nodeId);
          if (found) return found;
        }
      }
      return null;
    };

    const getDescendants = (node: SectorNode): string[] => {
      let ids: string[] = [];
      if (node.children) {
        for (const child of node.children) {
          ids.push(child.id);
          ids = ids.concat(getDescendants(child));
        }
      }
      return ids;
    };

    const currentSectors = new Set(formData.selectedSectors);
    const node = findNode(sectorsTree, id);
    const descendants = node ? getDescendants(node) : [];

    const getSectorName = (tid: string) => {
      const sector = sectorsTerms.data.find((s) => s.tid === tid);
      return sector?.name || tid;
    };

    const allNames = [getSectorName(id), ...descendants.map(getSectorName)];

    if (currentSectors.has(getSectorName(id))) {
      allNames.forEach((name) => currentSectors.delete(name));
    } else {
      allNames.forEach((name) => currentSectors.add(name));
    }

    setFormData((prev) => ({
      ...prev,
      selectedSectors: Array.from(currentSectors),
    }));
  };

  const renderSectorsTree = (nodes: SectorNode[], depth = 0): JSX.Element => (
    <ul
      className="sector-tree"
      style={{
        marginLeft: depth ? 16 : 0,
        paddingLeft: 0,
        listStyle: "none",
        margin: 0,
      }}
    >
      {nodes
        .filter(
          (n) =>
            !sectorSearch ||
            n.label.toLowerCase().includes(sectorSearch.toLowerCase()) ||
            (n.children &&
              n.children.length > 0 &&
              n.children.some((c) =>
                c.label.toLowerCase().includes(sectorSearch.toLowerCase()),
              )),
        )
        .map((n) => {
          const hasChildren = n.children && n.children.length > 0;
          const isExpanded = expandedSectors.has(n.id);
          const isChecked = formData.selectedSectors.includes(n.label);

          return (
            <li key={n.id} style={{ marginBottom: "0.25rem" }}>
              <div className="tree-row">
                {hasChildren ? (
                  <button
                    type="button"
                    className="tree-toggle"
                    onClick={() => handleToggleExpandSector(n.id)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "0.25rem",
                      marginRight: "0.5rem",
                      display: "flex",
                      alignItems: "center",
                      width: "24px",
                      height: "24px",
                    }}
                  >
                    <i
                      className={`pi ${
                        isExpanded ? "pi-chevron-down" : "pi-chevron-right"
                      }`}
                      style={{ fontSize: "0.75rem", color: "#6B7280" }}
                    />
                  </button>
                ) : (
                  <span
                    style={{
                      width: "24px",
                      height: "24px",
                      display: "inline-block",
                      marginRight: "0.5rem",
                    }}
                  />
                )}
                <Checkbox
                  inputId={`sector-tree-${n.id}`}
                  checked={isChecked}
                  onChange={() => toggleSector(n.id)}
                />
                <label
                  htmlFor={`sector-tree-${n.id}`}
                  style={{
                    marginLeft: "0.5rem",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    color: "#374151",
                    flex: 1,
                    marginBottom: 0,
                  }}
                >
                  {n.label}
                </label>
              </div>
              {hasChildren &&
                isExpanded &&
                renderSectorsTree(n.children!, depth + 1)}
            </li>
          );
        })}
    </ul>
  );

  const handleToggleExpandProgramme = (id: string) => {
    setExpandedProgrammes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleProgramme = (id: string) => {
    const findNode = (
      nodes: ProgrammeNode[],
      nodeId: string,
    ): ProgrammeNode | null => {
      for (const node of nodes) {
        if (node.id === nodeId) return node;
        if (node.children) {
          const found = findNode(node.children, nodeId);
          if (found) return found;
        }
      }
      return null;
    };

    const getDescendants = (node: ProgrammeNode): string[] => {
      let ids: string[] = [];
      if (node.children) {
        for (const child of node.children) {
          ids.push(child.id);
          ids = ids.concat(getDescendants(child));
        }
      }
      return ids;
    };

    const currentProgrammes = new Set(formData.selectedProgrammes || []);
    const node = findNode(programmesTree, id);
    const descendants = node ? getDescendants(node) : [];
    const allIds = [id, ...descendants];

    if (currentProgrammes.has(id)) {
      allIds.forEach((i) => currentProgrammes.delete(i));
    } else {
      allIds.forEach((i) => currentProgrammes.add(i));
    }

    setFormData((prev) => ({
      ...prev,
      selectedProgrammes: Array.from(currentProgrammes),
    }));
  };

  const renderProgrammesTree = (
    nodes: ProgrammeNode[],
    depth = 0,
  ): JSX.Element => (
    <ul
      className="programme-tree"
      style={{
        marginLeft: depth ? 16 : 0,
        paddingLeft: 0,
        listStyle: "none",
        margin: 0,
      }}
    >
      {nodes
        .filter(
          (n) =>
            !programmeSearch ||
            n.label.toLowerCase().includes(programmeSearch.toLowerCase()) ||
            (n.children &&
              n.children.length > 0 &&
              n.children.some((c) =>
                c.label.toLowerCase().includes(programmeSearch.toLowerCase()),
              )),
        )
        .map((n) => {
          const hasChildren = n.children && n.children.length > 0;
          const isExpanded = expandedProgrammes.has(n.id);

          return (
            <li key={n.id} style={{ marginBottom: "0.25rem" }}>
              <div className="tree-row">
                {hasChildren ? (
                  <button
                    type="button"
                    className="tree-toggle"
                    onClick={() => handleToggleExpandProgramme(n.id)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "0.25rem",
                      marginRight: "0.5rem",
                      display: "flex",
                      alignItems: "center",
                      width: "24px",
                      height: "24px",
                    }}
                  >
                    <i
                      className={`pi ${
                        isExpanded ? "pi-chevron-down" : "pi-chevron-right"
                      }`}
                      style={{ fontSize: "0.75rem", color: "#6B7280" }}
                    />
                  </button>
                ) : (
                  <span
                    style={{
                      width: "24px",
                      height: "24px",
                      display: "inline-block",
                      marginRight: "0.5rem",
                    }}
                  />
                )}
                <Checkbox
                  inputId={`programme-tree-${n.id}`}
                  checked={formData.selectedProgrammes?.includes(n.id) || false}
                  onChange={() => toggleProgramme(n.id)}
                />
                <label
                  htmlFor={`programme-tree-${n.id}`}
                  style={{
                    marginLeft: "0.5rem",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    color: "#374151",
                    flex: 1,
                    marginBottom: 0,
                  }}
                >
                  {n.label}
                </label>
              </div>
              {hasChildren &&
                isExpanded &&
                renderProgrammesTree(n.children!, depth + 1)}
            </li>
          );
        })}
    </ul>
  );

  const handleDocumentsChange = useCallback((fids: string[]) => {
    // console.log("Documents changed in parent:", fids);
    setFormData((prev) => ({ ...prev, documentFids: fids }));
  }, []);

  const handleSave = async () => {
    setSaveError(null);
    setErrors({});
    let validationFailed = false;
    let firstErrorField: string | null = null;

    if (!formData.organizationName.trim()) {
      setErrors((prev) => ({
        ...prev,
        organizationName: "Organization Name is required.",
      }));
      validationFailed = true;
      if (!firstErrorField) firstErrorField = "organizationName";
    }

    // remove validation check for city resolves issue 27177
    // if (!formData.city.trim()) {
    //   setErrors((prev) => ({ ...prev, city: "City is required." }));
    //   validationFailed = true;
    //   if (!firstErrorField) firstErrorField = "city";
    // }

    if (formData.selectedSectors.length === 0) {
      setErrors((prev) => ({
        ...prev,
        selectedSectors: "At least one sector is required.",
      }));
      validationFailed = true;
      if (!firstErrorField) firstErrorField = "selectedSectors";
    }

    if (validationFailed) {
      showToast({
        type: "error",
        text: "Please fill in all required fields.",
        duration: 5000,
      });

      if (firstErrorField) {
        const selectorMap: Record<string, string> = {
          organizationName: ".form-row-organization-name",
          city: ".form-row-city",
          selectedSectors: ".form-row-sectors",
        };
        const selector = selectorMap[firstErrorField];
        const errorElement = document.querySelector(selector);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
      return;
    }

    try {
      const organizationTypeTid =
        fundingTypeTerms.data.find(
          (term: Term) => term.name === formData.organizationType,
        )?.tid || "";

      const legalFormValue = formData.legalForm === "Public" ? true : false;

      const payload: any = {
        field_pic_number: formData.picNumber,
        title: formData.organizationName,
        field_type: organizationTypeTid ? [organizationTypeTid] : [],
        field_legal_form: legalFormValue,
        field_year_of_establishment: formData.yearOfEstablishment,
        field_company_size: formData.organizationSize
          ? [formData.organizationSize]
          : [],
        field_email: formData.contactEmail,
        field_country: [
          organizationMappers.mapCountryNameToId(
            formData.country,
            countriesTerms.data,
          ),
        ],
        field_city: formData.city,
        field_phone_number: formData.contactPhone,
        field_sectors: organizationMappers.mapSectorNamesToIds(
          formData.selectedSectors,
          sectorsTerms.data,
        ),
        field_expertise: formData.euExpertise ? "1" : "0",
        field_project_leader: formData.projectsAsLeader || null,
        field_project_partner: formData.projectsAsPartner || null,
        field_programme: formData.selectedProgrammes || [],
        field_description: formData.description,
        field_facebook: formData.socialProfiles.facebook || null,
        field_instagram: formData.socialProfiles.instagram || null,
        field_linkedin: formData.socialProfiles.linkedin || null,
        field_twitter: formData.socialProfiles.twitter || null,
        field_youtube: formData.socialProfiles.youtube || null,
        field_website_url: formData.socialProfiles.website || null,
        field_documents:
          formData.documentFids.length > 0
            ? formData.documentFids.map((fid) => ({ target_id: fid }))
            : null,
      };

      if (formData.logoFid) {
        payload.field_logo = formData.logoFid;
      }

      // console.log("Saving with documents:", formData.documentFids);

      await onSave(payload);

      showToast({
        type: "success",
        text: "Organization information updated successfully!",
        duration: 5000,
      });
    } catch (error: any) {
      setSaveError(error.message || "Failed to update organization info");
    }
  };

  return (
    <div className="org-form-new">
      {/* Header with Actions */}
      <div className="form-header-new">
        <div className="header-content">
          <h2>Organisation details</h2>
          <p>Update your organisation details here</p>
        </div>
        <div className="header-actions">
          <Button label="Cancel" className="p-button-outlined" />
          <Button
            label={saveLoading ? "Saving..." : "Save"}
            loading={saveLoading}
            disabled={saveLoading}
            onClick={handleSave}
          />
        </div>
      </div>

      {/* Form Grid Layout */}
      <div className="form-grid">
        {/* PIC Number */}
        <div className="form-row">
          <div className="form-labels">
            <div className="form-label-item">
              <label>
                Participant Identification Code Number (PIC) (optional)
              </label>
            </div>
          </div>
          <div className="form-inputs">
            <div className="form-input-item">
              <InputText
                value={formData.picNumber}
                placeholder="PIC number"
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    picNumber: e.target.value,
                  }))
                }
              />
              <p className="input-hint">
                Don't have a PIC number?{" "}
                <a href="/settings/subscription">Get Access Now!</a> Upgrade to
                Premium Plan and let the EUCalls expert team to take care of the
                registration process.
              </p>
            </div>
          </div>
        </div>

        {/* Organisation Name */}
        <div className="form-row form-row-organization-name">
          <div className="form-labels">
            <div className="form-label-item">
              <label>Organisation name</label>
              <p className="label-description">
                This will be displayed on your profile.
              </p>
            </div>
          </div>
          <div className="form-inputs">
            <div className="form-input-item">
              <InputText
                value={formData.organizationName}
                placeholder="Organization Name"
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    organizationName: e.target.value,
                  }))
                }
                required
                className={errors.organizationName ? "p-invalid" : ""}
              />
              {errors.organizationName && (
                <small className="p-error">{errors.organizationName}</small>
              )}
            </div>
          </div>
        </div>

        {/* Logo */}
        <div className="form-row">
          <div className="form-labels">
            <div className="form-label-item">
              <label>Organisation logo (optional)</label>
            </div>
          </div>
          <div className="form-inputs">
            <div className="form-input-item">
              <LogoUpload
                src={formData.logoUrl}
                onChange={(fid) =>
                  setFormData((prev) => ({ ...prev, logoFid: fid }))
                }
              />
              <p className="input-hint">
                Click to upload or drag and drop
                <br />
                SVG, PNG, JPG or GIF (max. 800x400px)
              </p>
            </div>
          </div>
        </div>

        {/* Type */}
        <div className="form-row">
          <div className="form-labels">
            <div className="form-label-item">
              <label>Type</label>
            </div>
          </div>
          <div className="form-inputs">
            <div className="form-input-item">
              <Dropdown
                value={formData.organizationType}
                options={fundingTypeOptions}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    organizationType: e.value,
                  }))
                }
                placeholder="Select type"
              />
            </div>
          </div>
        </div>

        {/* Year */}
        <div className="form-row">
          <div className="form-labels">
            <div className="form-label-item">
              <label>Year of establishment (optional)</label>
            </div>
          </div>
          <div className="form-inputs">
            <div className="form-input-item">
              <Dropdown
                value={formData.yearOfEstablishment}
                options={yearOptions}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    yearOfEstablishment: e.value,
                  }))
                }
                placeholder="Year of Establishment"
              />
            </div>
          </div>
        </div>

        {/* Legal Form */}
        <div className="form-row">
          <div className="form-labels">
            <div className="form-label-item">
              <label>Legal form</label>
            </div>
          </div>
          <div className="form-inputs">
            <div className="form-input-item">
              <Dropdown
                value={formData.legalForm}
                options={organizationTypes}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, legalForm: e.value }))
                }
                placeholder="Private"
              />
            </div>
          </div>
        </div>

        {/* Size */}
        <div className="form-row">
          <div className="form-labels">
            <div className="form-label-item">
              <label>Size (optional)</label>
              <p className="label-description">Number of employees</p>
            </div>
          </div>
          <div className="form-inputs">
            <div className="form-input-item">
              <div className="size-buttons">
                {organizationSizes.map((size) => {
                  const isActive = formData.organizationSize === size.tid;
                  return (
                    <Button
                      key={size.tid}
                      label={size.name}
                      className={`size-btn ${
                        isActive ? "p-button-primary" : "p-button-outlined"
                      }`}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          organizationSize: size.tid,
                        }))
                      }
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Contact Email */}
        <div className="form-row">
          <div className="form-labels">
            <div className="form-label-item">
              <label>Contact email (optional)</label>
            </div>
          </div>
          <div className="form-inputs">
            <div className="form-input-item">
              <InputText
                value={formData.contactEmail}
                placeholder="info@eucalls.net"
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    contactEmail: e.target.value,
                  }))
                }
              />
            </div>
          </div>
        </div>

        {/* Country and City */}
        <div className="form-row form-row-city">
          <div className="form-labels">
            <div className="form-label-item">
              <label>Country and city</label>
            </div>
          </div>
          <div className="form-inputs">
            <div className="form-input-item">
              <div className="country-city-grid">
                <Dropdown
                  value={formData.country}
                  options={countryOptions}
                  onChange={(e) => {
                    // console.log(e, "e");
                    setFormData((prev) => ({ ...prev, country: e.value }));
                  }}
                  placeholder="Greece"
                  filter
                />

                <InputText
                  value={formData.city}
                  placeholder="City"
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, city: e.target.value }))
                  }
                  required
                  className={errors.city ? "p-invalid" : ""}
                />
                <div></div>
                <div>
                  {errors.city && (
                    <small className="p-error">{errors.city}</small>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Phone */}
        <div className="form-row">
          <div className="form-labels">
            <div className="form-label-item">
              <label>Contact phone number (optional)</label>
            </div>
          </div>
          <div className="form-inputs">
            <div className="form-input-item">
              <div className="p-inputgroup">
                <Dropdown
                  value={selectedCountryCode.value}
                  options={countryPhoneCodes}
                  onChange={(e) => {
                    setSelectedCountryCode(e.value);
                    const currentPhone = formData.contactPhone || "";
                    const cleanPhone = currentPhone.replace(
                      /^\+?\d{1,4}\s*/,
                      "",
                    );
                    const selectedCountry = countryPhoneCodes.find(
                      (country) => country.value === e.value,
                    );
                    if (selectedCountry) {
                      const newPhoneNumber =
                        `${selectedCountry.code} ${cleanPhone}`.trim();
                      setFormData((prev) => ({
                        ...prev,
                        contactPhone: newPhoneNumber,
                      }));
                    }
                  }}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="GR"
                  className="country-code-dropdown"
                />
                <InputText
                  value={formData.contactPhone}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      contactPhone: e.target.value,
                    }))
                  }
                  placeholder="+30 690000000"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sectors */}
        <div className="form-row form-row-sectors">
          <div className="form-labels">
            <div className="form-label-item">
              <label>Sectors</label>
            </div>
          </div>
          <div className="form-inputs">
            <div className="form-input-item">
              <div className="sectors-wrapper">
                {formData.selectedSectors.length > 0 && (
                  <div className="selected-chips">
                    {formData.selectedSectors.map((sector, index) => (
                      <div key={`${sector}-${index}`} className="chip">
                        <span>{sector}</span>
                        <button
                          type="button"
                          onClick={() => handleSectorRemove(sector)}
                          className="chip-remove"
                        >
                          <i className="pi pi-times" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="search-box">
                  <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                      value={sectorSearch}
                      onChange={(e) => setSectorSearch(e.target.value)}
                      placeholder="Search for Sectors..."
                    />
                  </span>
                </div>

                <div className="sectors-tree-wrap">
                  {renderSectorsTree(sectorsTree)}
                </div>
                {errors.selectedSectors && (
                  <small className="p-error">{errors.selectedSectors}</small>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* EU Expertise */}
        <div className="form-row">
          <div className="form-labels">
            <div className="form-label-item">
              <label>EU expertise (optional)</label>
            </div>
          </div>
          <div className="form-inputs">
            <div className="form-input-item">
              <div className="eu-expertise-wrapper">
                <div className="switch-row">
                  <InputSwitch
                    checked={formData.euExpertise}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        euExpertise: e.value,
                      }))
                    }
                  />
                  <label>We have implemented EU funded projects</label>
                </div>

                {formData.euExpertise && (
                  <div className="eu-expertise-expanded">
                    <div className="projects-row">
                      <div className="project-field">
                        <label>Number of projects as leader</label>
                        <InputText
                          value={formData.projectsAsLeader || ""}
                          placeholder="12"
                          type="number"
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              projectsAsLeader: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="project-field">
                        <label>Number of projects as partner</label>
                        <InputText
                          value={formData.projectsAsPartner || ""}
                          placeholder="20"
                          type="number"
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              projectsAsPartner: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>

                    <div className="programmes-field">
                      <label>Programmes</label>

                      {formData.selectedProgrammes &&
                        formData.selectedProgrammes.length > 0 && (
                          <div className="selected-chips">
                            {formData.selectedProgrammes.map((programmeTid) => {
                              const findLabel = (
                                nodes: ProgrammeNode[],
                              ): string | null => {
                                for (const n of nodes) {
                                  if (n.id === programmeTid) return n.label;
                                  if (n.children) {
                                    const match = findLabel(n.children);
                                    if (match) return match;
                                  }
                                }
                                return null;
                              };
                              const label =
                                findLabel(programmesTree) || programmeTid;

                              return (
                                <div key={programmeTid} className="chip">
                                  <span>{label}</span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      toggleProgramme(programmeTid)
                                    }
                                    className="chip-remove"
                                  >
                                    <i className="pi pi-times" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}

                      <div className="search-box">
                        <span className="p-input-icon-left">
                          <i className="pi pi-search" />
                          <InputText
                            value={programmeSearch}
                            onChange={(e) => setProgrammeSearch(e.target.value)}
                            placeholder="Search for programmes..."
                          />
                        </span>
                      </div>

                      <div className="programmes-tree-wrap">
                        {renderProgrammesTree(programmesTree)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="form-row">
          <div className="form-labels">
            <div className="form-label-item">
              <label>Description (optional)</label>
              <p className="label-description">
                A quick snapshot of your organisation.
              </p>
            </div>
          </div>
          <div className="form-inputs">
            <div className="form-input-item">
              <div className="bio-editor">
                <ReactQuill
                  theme="snow"
                  value={formData.description}
                  onChange={(content) => {
                    // if (getTextLength(content) <= descriptionMaxLength) {
                    setFormData((prev) => ({
                      ...prev,
                      description: content,
                    }));
                    // }
                  }}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Write a short description of your organisation..."
                  className="custom-quill-editor"
                />
                {/* remove character limit for description to resolve issue 27178 */}
                {/* <div className="char-count">
                  {descriptionMaxLength - getTextLength(formData.description)}{" "}
                  characters left
                </div> */}
              </div>
              <p className="input-hint">
                If the text area is prefilled it's because you've added your PIC
                number
              </p>
            </div>
          </div>
        </div>

        {/* Documents */}
        <div className="form-row">
          <div className="form-labels">
            <div className="form-label-item">
              <label>Uploaded documents (optional)</label>
              <p className="label-description">
                Share a few snapshots of your work.
              </p>
            </div>
          </div>
          <div className="form-inputs">
            <div className="form-input-item">
              <DocumentUpload
                onChange={handleDocumentsChange}
                initialDocuments={initialDocuments}
              />
            </div>
          </div>
        </div>

        {/* Social Profiles */}
        <div className="form-row">
          <div className="form-labels">
            <div className="form-label-item">
              <label>Social profiles (optional)</label>
            </div>
          </div>
          <div className="form-inputs">
            <div className="form-input-item">
              <div className="social-profiles">
                <div className="social-input">
                  <span className="social-icon">
                    <i className="pi pi-linkedin" />
                  </span>
                  <InputText
                    value={formData.socialProfiles.linkedin}
                    placeholder="in.com/in/"
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        socialProfiles: {
                          ...prev.socialProfiles,
                          linkedin: e.target.value,
                        },
                      }))
                    }
                  />
                </div>

                <div className="social-input">
                  <span className="social-icon">
                    <i className="pi pi-twitter" />
                  </span>
                  <InputText
                    value={formData.socialProfiles.twitter}
                    placeholder="x.com/in/"
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        socialProfiles: {
                          ...prev.socialProfiles,
                          twitter: e.target.value,
                        },
                      }))
                    }
                  />
                </div>

                <div className="social-input">
                  <span className="social-icon">
                    <i className="pi pi-youtube" />
                  </span>
                  <InputText
                    value={formData.socialProfiles.youtube}
                    placeholder="youtube.com/"
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        socialProfiles: {
                          ...prev.socialProfiles,
                          youtube: e.target.value,
                        },
                      }))
                    }
                  />
                </div>

                <div className="social-input">
                  <span className="social-icon">
                    <i className="pi pi-instagram" />
                  </span>
                  <InputText
                    value={formData.socialProfiles.instagram}
                    placeholder="instagram.com/"
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        socialProfiles: {
                          ...prev.socialProfiles,
                          instagram: e.target.value,
                        },
                      }))
                    }
                  />
                </div>

                <div className="social-input">
                  <span className="social-icon">
                    <i className="pi pi-facebook" />
                  </span>
                  <InputText
                    value={formData.socialProfiles.facebook}
                    placeholder="facebook.com/"
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        socialProfiles: {
                          ...prev.socialProfiles,
                          facebook: e.target.value,
                        },
                      }))
                    }
                  />
                </div>

                <div className="social-input">
                  <span className="social-icon">🌐</span>
                  <InputText
                    value={formData.socialProfiles.website}
                    placeholder="eucall.net"
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        socialProfiles: {
                          ...prev.socialProfiles,
                          website: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Error */}
      {saveError && <div className="error-banner">{saveError}</div>}

      {/* Bottom Actions */}
      <div className="form-footer">
        <Button label="Cancel" className="p-button-outlined" />
        <Button
          label={saveLoading ? "Saving..." : "Save"}
          loading={saveLoading}
          disabled={saveLoading}
          onClick={handleSave}
        />
      </div>
      {/* Delete Organization Section */}
      <div className=" delete-section">
        <div className="form-labels">
          <div className="form-label-item">
            <label>Delete organisation</label>
            <p className="label-description">
              If you delete your organisation you'll lose all your activity and
              will not be able to recover it in the future.
            </p>
          </div>
        </div>
        <div className="form-inputs">
          <div
            className="form-input-item"
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              label="Delete organisation"
              severity="danger"
              className="p-button-danger delete-org-btn"
              onClick={onShowDeleteDialog}
            />
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        visible={showDeleteDialog}
        style={{ width: "500px" }}
        onHide={onCancelDelete}
        showHeader={false}
        closable={false}
        className="delete-org-dialog"
      >
        <div className="delete-dialog-content">
          <button className="close-button" onClick={onCancelDelete}>
            <i className="pi pi-times" />
          </button>

          <div className="dialog-body">
            <div className="icon-wrapper">
              <i className="pi pi-exclamation-circle" />
            </div>

            <div className="dialog-text">
              <h3>Are you sure you want to delete this organisation?</h3>
              <p>
                All organisation data will be permanently deleted and cannot be
                recovered. Also, any existing members will be removed.
              </p>
            </div>
          </div>

          <div className="dialog-actions">
            <Button
              label="Cancel"
              onClick={onCancelDelete}
              className="p-button-outlined cancel-btn"
            />
            <Button
              label={deleteLoading ? "Deleting..." : "Delete"}
              loading={deleteLoading}
              disabled={deleteLoading}
              onClick={onDelete}
              className="delete-btn"
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};
